import type { Timezone } from 'next-intl';
import { createDebug } from '../../../debug/src/utils/debug';

type DateStatus = { isExpired: boolean; isWithinNext24Hours: boolean };
type DateFormat = { showHours: boolean; showMinutes: boolean; showSeconds: boolean };

const debug = createDebug({ isEnabled: false });

// Time conversion constants - defined locally to avoid circular dependency with @shared/components
const MILLISECONDS_IN_SECOND = 1000;
const MILLISECONDS_IN_MINUTE = 1000 * 60;
const MILLISECONDS_IN_HOUR = 1000 * 60 * 60;
const HOURS_IN_DAY = 24;

/**
 * Parses a date string or Date object into a valid Date instance
 * @param date - The date to parse (Date object or string)
 * @param preserveTime - If false, strips time from ISO strings (default: false for backwards compatibility)
 * @returns Parsed Date object or null if invalid
 */
const parseDate = (date?: Date | string, preserveTime: boolean = false): Date | null => {
  if (!date) return null;

  try {
    if (date instanceof Date) {
      return isNaN(date.getTime()) ? null : date;
    }

    if (typeof date === 'string') {
      const trimmedDate = date.trim();
      if (!trimmedDate) return null;

      // Handle ISO strings - strip time if preserveTime is false (for display purposes)
      let dateString = trimmedDate;
      if (!preserveTime && trimmedDate.includes('T')) {
        dateString = trimmedDate.split('T')[0] || trimmedDate;
      }

      const parsedDate = new Date(dateString);
      return isNaN(parsedDate.getTime()) ? null : parsedDate;
    }

    return null;
  } catch (error) {
    debug.error('Error parsing date:', error, 'Date:', date);
    return null;
  }
};

/**
 * Formats a date as a countdown string without leading zeros (e.g. `2h 30m`, `9m 2s`)
 * @returns Formatted countdown string
 */
const formatCountdown = (date: Date, currentTime: Date = new Date()): string => {
  const diff = date.getTime() - currentTime.getTime();

  if (diff <= 0) return '0h 0m 0s';

  const format: DateFormat = diff >= MILLISECONDS_IN_HOUR ? { showHours: true, showMinutes: true, showSeconds: false } : { showHours: false, showMinutes: true, showSeconds: true };

  const dateParts: string[] = [];

  if (format.showHours) dateParts.push(`${Math.floor(diff / MILLISECONDS_IN_HOUR)}h`);
  if (format.showMinutes) dateParts.push(`${Math.floor((diff % MILLISECONDS_IN_HOUR) / MILLISECONDS_IN_MINUTE)}m`);
  if (format.showSeconds) dateParts.push(`${Math.floor((diff % MILLISECONDS_IN_MINUTE) / MILLISECONDS_IN_SECOND)}s`);

  return dateParts.join(' ');
};

/**
 * Formats a date in standard format
 * @param monthFormat - 'long' for full month name, 'short' for abbreviated (e.g., 'Jan')
 * @param includeYear - Whether to include the year in the formatted string (default: true)
 * @returns Formatted date string
 */
const formatStandardDate = (date: Date, locale: string, monthFormat: 'long' | 'short' = 'short', includeYear: boolean = true): string => {
  const day = date.getDate();
  let month = date.toLocaleString(locale, { month: monthFormat });
  // Normalize to 3-character abbreviation for 'short' format (e.g., 'Sept' -> 'Sep')
  if (monthFormat === 'short') {
    month = month.substring(0, 3);
  }
  const year = date.getFullYear();
  return includeYear ? `${day} ${month} ${year}` : `${day} ${month}`;
};

/**
 * Calculates time difference and returns hours and minutes
 * @returns Object with hours and minutes until target date
 */
const calculateTimeUntil = (targetDate: Date, currentTime: Date = new Date()): { hours: number; minutes: number } => {
  const diff = targetDate.getTime() - currentTime.getTime();
  const hours = Math.floor(diff / MILLISECONDS_IN_HOUR);
  const minutes = Math.floor((diff % MILLISECONDS_IN_HOUR) / MILLISECONDS_IN_MINUTE);
  return { hours, minutes };
};

/**
 * Determines the status of a date relative to current time
 * @returns Object containing expiry and 24-hour status
 */
const getDateStatus = (date: Date, currentTime: Date = new Date()): DateStatus => {
  const twentyFourHoursFromNow = new Date(currentTime.getTime() + HOURS_IN_DAY * MILLISECONDS_IN_HOUR);

  return {
    isExpired: date < currentTime,
    isWithinNext24Hours: date > currentTime && date < twentyFourHoursFromNow,
  };
};

/**
 * Converts minutes to seconds
 * @param minutes - Number of minutes to convert
 * @returns Number of seconds
 */
const minutesToSeconds = (minutes: number): number => minutes * 60;

/**
 * Converts hours to seconds
 * @param hours - Number of hours to convert
 * @returns Number of seconds
 */
const hoursToSeconds = (hours: number): number => hours * 60 * 60;

/**
 * Converts days to seconds
 * @param days - Number of days to convert
 * @returns Number of seconds
 */
const daysToSeconds = (days: number): number => days * 24 * 60 * 60;

/**
 * Determines if a timer should be shown for an expiration date
 * @param expirationDate - The expiration date (string or Date)
 * @param currentTime - The current time (defaults to now)
 * @returns true if the expiration date is within the next 24 hours and not expired
 */
const shouldShowTimer = (expirationDate?: Date | string | null, currentTime: Date = new Date()): boolean => {
  if (!expirationDate) return false;

  // Parse with time preserved for accurate 24-hour calculation
  const parsedDate = parseDate(expirationDate, true);
  if (!parsedDate) return false;

  const { isExpired, isWithinNext24Hours } = getDateStatus(parsedDate, currentTime);
  return !isExpired && isWithinNext24Hours;
};

/**
 * Extracts the date number from a date the provided timeZone.
 * @param date - The date to extract the number from
 * @param timeZone - The time zone to use for the extraction
 * @returns The date number (1-31)
 */
const getDateNumber = (date: Date, timeZone: Timezone | undefined): number => {
  return parseInt(new Intl.DateTimeFormat('en', { day: 'numeric', timeZone }).format(date), 10);
};

/**
 * Extracts the month number from a date the provided timeZone.
 * @param date - The date to extract the number from
 * @param timeZone - The time zone to use for the extraction
 * @returns The month number (0-11)
 */
const getMonthNumber = (date: Date, timeZone: Timezone | undefined): number => {
  return parseInt(new Intl.DateTimeFormat('en', { month: 'numeric', timeZone }).format(date), 10) - 1;
};

/**
 * Extracts the full year from a date the provided timeZone.
 * @param date - The date to extract the year from
 * @param timeZone - The time zone to use for the extraction
 */
const getFullYear = (date: Date, timeZone: Timezone | undefined): number => {
  return parseInt(new Intl.DateTimeFormat('en', { year: 'numeric', timeZone }).format(date), 10);
};

/**
 * Returns whether date1 and date2 are the same day in the provided timeZone.
 * @param date1 - The first date to compare
 * @param date2 - The second date to compare
 * @param timeZone - The time zone to use for the comparison
 */
const isSameDay = (date1: Date, date2: Date, timeZone: Timezone | undefined): boolean => {
  return (
    getDateNumber(date1, timeZone) === getDateNumber(date2, timeZone) &&
    getMonthNumber(date1, timeZone) === getMonthNumber(date2, timeZone) &&
    getFullYear(date1, timeZone) === getFullYear(date2, timeZone)
  );
};

export {
  calculateTimeUntil,
  daysToSeconds,
  formatCountdown,
  formatStandardDate,
  getDateNumber,
  getDateStatus,
  getFullYear,
  getMonthNumber,
  hoursToSeconds,
  isSameDay,
  minutesToSeconds,
  parseDate,
  shouldShowTimer,
};
